import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  IconButton,
} from '@mui/material';
import { ArrowBack, Edit, Inventory, Notes } from '@mui/icons-material';
import { api } from '../../services/api';

interface ItemDetail {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  units_per_carton: number;
  min_stock: number;
  remark?: string;
  is_enabled: boolean;
  created_at: string;
  updated_at?: string;
}

const ItemDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await api.get(`/api/items/${id}`);
      setItem(response.data);
    } catch (error) {
      console.error('Error fetching item:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Paper sx={{ p: 4, m: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Item not found
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back to Items
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Item Details
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/items/${id}/edit`)}
        >
          Edit Item
        </Button>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        {/* General Information Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Inventory color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">General Information</Typography>
        </Box>

        <Table>
          <TableBody>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', width: 200, bgcolor: 'grey.50' }}
              >
                ID
              </TableCell>
              <TableCell>{item.id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
              >
                Category
              </TableCell>
              <TableCell>
                <Chip label={item.category_name} size="small" color="primary" variant="outlined" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
              >
                Item Name
              </TableCell>
              <TableCell>
                <Typography fontWeight="medium">{item.name}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
              >
                Units per Carton
              </TableCell>
              <TableCell>{item.units_per_carton}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
              >
                Minimum Stock Threshold
              </TableCell>
              <TableCell>{item.min_stock}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}
              >
                Status
              </TableCell>
              <TableCell>
                <Chip
                  label={item.is_enabled ? 'Active' : 'Inactive'}
                  color={item.is_enabled ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ my: 4 }} />

        {/* Note/Specification Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Notes color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Note / Specification</Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, minHeight: 80 }}>
          {item.remark ? (
            <Typography>{item.remark}</Typography>
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              No notes or specifications added
            </Typography>
          )}
        </Box>

        {/* Timestamps */}
        <Divider sx={{ my: 4 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body2">
              {new Date(item.created_at).toLocaleString()}
            </Typography>
          </Grid>
          {item.updated_at && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {new Date(item.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ItemDetailPage;
